import React, { useEffect, useState } from 'react';

import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableFooter from '@material-ui/core/TableFooter';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';

import type { HrisImportLog } from '../typescript-types/hris-import-log';

import { loadHrisImportLogs } from './actions/load-hris-import-logs';
import {
  hrisImportLogForMergeIntegrationIdSelector,
  hrisImportLogsStateForMergeIntegrationIdSelector,
} from './hris-import-log-selectors';
import { userSelector } from '../reducers/current-user-selectors';

import { HrisEmployeeDiffDialog } from './hris-employee-diff/hris-employee-diff-dialog';

import { createDisplayStatus } from './util/create-display-status';
import { getEmployeeChangeLogCountForOrg } from './util/get-employee-change-log-counts';
import { makeDurationLabel } from './util/make-duration-label';
import { formatDate } from '../util/formatters';
import { sortByDate } from '../util/sort-functions';
import { LoadingMessage } from '../widgets/loading-message';

/**
 * The number of logs visible on each page of the table
 */
const HRIS_IMPORT_LOG_PAGE_SIZE = 5;

type HrisImportLogsTableProps = {
  mergeIntegrationId: number;
  orgIdFilter: number | null;
};

export const HrisImportLogsTable = ({
  mergeIntegrationId,
  orgIdFilter,
}: HrisImportLogsTableProps) => {
  // zero-indexed
  const [pageNumber, setPageNumber] = useState(0);
  const [selectedHrisImportLog, setSelectedHrisImportLog] = useState<HrisImportLog | null>(null);

  const dispatch = useDispatch();

  const currentUser = useSelector(userSelector);

  const allHrisImportLogs = useSelector((state: any) =>
    hrisImportLogForMergeIntegrationIdSelector(state, mergeIntegrationId),
  );

  const sortedHrisImportLogs = (allHrisImportLogs || []).sort(sortByDate((log) => log.createdAt));

  const hrisImportLogs = sortedHrisImportLogs.slice(
    pageNumber * HRIS_IMPORT_LOG_PAGE_SIZE,
    (pageNumber + 1) * HRIS_IMPORT_LOG_PAGE_SIZE,
  );

  const pageState = useSelector((state: any) =>
    hrisImportLogsStateForMergeIntegrationIdSelector(state, mergeIntegrationId, pageNumber),
  );
  const loading = pageState ? pageState.loading : true;

  useEffect(() => {
    dispatch(loadHrisImportLogs(mergeIntegrationId));
  });

  const handlePageChange: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    page: number,
  ) => void = (_event, page) => {
    setPageNumber(page);
  };

  const handleEmployeesChangedClick = (hrisImportLog: HrisImportLog | null) => () => {
    setSelectedHrisImportLog(hrisImportLog);
  };

  const emptyRows = HRIS_IMPORT_LOG_PAGE_SIZE - hrisImportLogs.length;

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="left">
                <div className="typo-bold">Started At</div>
              </TableCell>
              <TableCell align="left">
                <div className="typo-bold">Import Status</div>
              </TableCell>
              <TableCell align="left">
                <div className="typo-bold">Employees Changed</div>
              </TableCell>
              <TableCell align="left">
                <div className="typo-bold">Finished At</div>
              </TableCell>
              <TableCell align="left">
                <div className="typo-bold">Duration</div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="u-height-350px">
            {hrisImportLogs.length ? (
              hrisImportLogs.map((hrisImportLog) => {
                const {
                  hrisImportLogId,
                  createdAt,
                  importStatus,
                  duration,
                  finishedAt,
                  countOfEmployeeChangeLogs,
                } = hrisImportLog;
                const changeLogCounts = getEmployeeChangeLogCountForOrg(
                  countOfEmployeeChangeLogs,
                  orgIdFilter,
                );

                return (
                  <TableRow key={hrisImportLogId}>
                    <TableCell>
                      {formatDate(moment(createdAt).toLocaleString(), 'MMM Do YYYY [at] h:mm:ss a')}
                    </TableCell>
                    <TableCell align="left">
                      {createDisplayStatus(importStatus, currentUser)}
                    </TableCell>
                    <TableCell align="left">
                      {importStatus ? (
                        <span
                          className="typo-link"
                          role="presentation"
                          onClick={handleEmployeesChangedClick(hrisImportLog)}
                        >
                          {' '}
                          {changeLogCounts}
                        </span>
                      ) : (
                        ''
                      )}
                    </TableCell>
                    <TableCell align="left">
                      {finishedAt
                        ? formatDate(
                            moment(finishedAt).toLocaleString(),
                            'MMM Do YYYY [at] h:mm:ss a',
                          )
                        : ''}
                    </TableCell>
                    <TableCell align="left">{makeDurationLabel(duration)}</TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell align="center" colSpan={5} rowSpan={5} className="u-overflow-hidden">
                  {loading ? <LoadingMessage /> : 'No imports found'}
                </TableCell>
              </TableRow>
            )}
            {emptyRows > 0 && (
              <TableRow style={{ height: `${80 * emptyRows}px` }}>
                <TableCell colSpan={5} />
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={sortedHrisImportLogs.length}
                rowsPerPage={HRIS_IMPORT_LOG_PAGE_SIZE}
                page={pageNumber}
                onPageChange={handlePageChange}
                // turns off the rows per page selector
                rowsPerPageOptions={[]}
                backIconButtonProps={loading ? { disabled: true } : undefined}
                nextIconButtonProps={loading ? { disabled: true } : undefined}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      {selectedHrisImportLog && (
        <HrisEmployeeDiffDialog
          hrisImportLog={selectedHrisImportLog}
          filterForOrgId={orgIdFilter}
          onClose={handleEmployeesChangedClick(null)}
        />
      )}
    </>
  );
};
